import { Box, ButtonBase, Skeleton, Typography } from "@mui/material";
import { amber, green, red } from "@mui/material/colors";
import { alpha, type Theme } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { SalesMissionKind, SalesMissionStatus } from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { salesApi } from "../client.js";
import { SalesErrorState } from "../components/SalesEmptyState.js";
import { VerticalBadge } from "../components/VerticalBadge.js";
import type { SxStyle } from "../leads/heatStyles.js";

const PAGE_SIZE = 10;

const STATUS_ORDER: Record<SalesMissionStatus, number> = {
  running: 0,
  pending: 1,
  failed: 2,
  done: 3,
};

type StatusFilter = "all" | SalesMissionStatus;

/**
 * Brain-tab section listing the autonomous loop's mission backlog.
 * Each row shows kind + vertical/geo + hypothesis + status. Operators
 * with `sales:edit` can run a pending mission immediately rather than
 * waiting for the planner to pick it.
 *
 * Status filter pills + 10-per-page pagination so the column stays
 * skimmable when 60+ missions accumulate. Sort puts running missions
 * at the top, then pending, failed last.
 */
export function MissionsList(): JSX.Element {
  const canEdit = useCan("sales:edit");
  const qc = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(0);

  const missions = useQuery({
    queryKey: ["sales", "missions"],
    queryFn: salesApi.getBrainMissions,
    refetchInterval: 30_000,
  });

  const runMutation = useMutation({
    mutationFn: (id: string) => salesApi.runMission(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sales", "missions"] });
      qc.invalidateQueries({ queryKey: ["sales", "brain-feed"] });
    },
  });

  if (missions.isLoading) {
    return <Skeleton variant="rounded" sx={{ height: 192, borderRadius: 1 }} />;
  }
  if (missions.isError) {
    return <SalesErrorState error={missions.error} surface="brain" />;
  }
  const all = missions.data?.items ?? [];

  // Counts by status for the filter pills.
  const counts = {
    all: all.length,
    running: all.filter((m) => m.status === "running").length,
    pending: all.filter((m) => m.status === "pending").length,
    done: all.filter((m) => m.status === "done").length,
    failed: all.filter((m) => m.status === "failed").length,
  };

  const filtered =
    filter === "all" ? all : all.filter((m) => m.status === filter);
  const sorted = [...filtered].sort((a, b) => {
    const sa = STATUS_ORDER[a.status] ?? 9;
    const sb = STATUS_ORDER[b.status] ?? 9;
    if (sa !== sb) return sa - sb;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * PAGE_SIZE;
  const slice = sorted.slice(start, start + PAGE_SIZE);

  return (
    <Box
      component="section"
      aria-label="Mission backlog"
      sx={{
        display: "grid",
        gridAutoRows: "min-content",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        p: 2,
      }}
    >
      <Box component="header" sx={{ display: "grid", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5 }}>
          <Typography component="h2" sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
            Mission backlog ({all.length})
          </Typography>
          <Box component="span" sx={{ fontSize: 10, color: "text.disabled" }}>
            polled every 30 s
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
          {(
            [
              ["all",     `All (${counts.all})`],
              ["running", `Running (${counts.running})`],
              ["pending", `Queued (${counts.pending})`],
              ["done",    `Done (${counts.done})`],
              ["failed",  `Failed (${counts.failed})`],
            ] as Array<[StatusFilter, string]>
          ).map(([k, label]) => (
            <FilterPill
              key={k}
              active={filter === k}
              onClick={() => {
                setFilter(k);
                setPage(0);
              }}
            >
              {label}
            </FilterPill>
          ))}
        </Box>
      </Box>

      {sorted.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          {filter === "all"
            ? "The orchestrator hasn't queued any missions yet."
            : `No missions in '${filter}'.`}
        </Typography>
      ) : (
        <>
          <Box component="ul" sx={{ display: "grid", gap: 0.75, listStyle: "none", m: 0, p: 0 }}>
            {slice.map((m) => (
              <Box
                component="li"
                key={m.id}
                sx={{
                  display: "grid",
                  gap: 0.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "background.default",
                  p: 1.5,
                  fontSize: 12,
                }}
              >
                <Box component="header" sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1, rowGap: 0.5 }}>
                  <Box component="span" sx={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
                    {kindLabel(m.kind)}
                  </Box>
                  {m.vertical && <VerticalBadge vertical={m.vertical} />}
                  {m.geography && (
                    <Box component="span" sx={{ fontSize: 10, color: "text.disabled" }}>
                      {m.geography}
                    </Box>
                  )}
                  <StatusPill status={m.status} />
                  {canEdit && m.status === "pending" && (
                    <ButtonBase
                      onClick={() => runMutation.mutate(m.id)}
                      disabled={runMutation.isPending}
                      sx={{
                        ml: "auto",
                        borderRadius: 1,
                        border: 1,
                        borderColor: "divider",
                        px: 1,
                        py: 0.25,
                        fontSize: 10,
                        fontWeight: 500,
                        color: "text.primary",
                        "&:hover": { bgcolor: "surfaceSoft" },
                        "&.Mui-disabled": { opacity: 0.5 },
                      }}
                    >
                      Run now
                    </ButtonBase>
                  )}
                </Box>
                <Typography sx={{ color: "text.primary" }}>{m.hypothesis}</Typography>
                {m.started_at && (
                  <Box component="span" sx={{ fontSize: 10, color: "text.disabled" }}>
                    started {new Date(m.started_at).toLocaleString()}
                    {m.finished_at &&
                      ` · finished ${new Date(m.finished_at).toLocaleString()}`}
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          <Box component="footer" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, pt: 0.5, fontSize: 10, color: "text.disabled" }}>
            <Box component="span">
              {start + 1}–{Math.min(start + PAGE_SIZE, sorted.length)} of {sorted.length}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PageButton
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← prev
              </PageButton>
              <PageButton
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              >
                next →
              </PageButton>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={active}
      sx={[
        { borderRadius: 999, px: 1, py: 0.25, fontSize: 10, fontWeight: 500, transition: "color 150ms, background-color 150ms" },
        active
          ? { bgcolor: "primary.main", color: "primary.contrastText" }
          : { border: 1, borderColor: "divider", color: "text.secondary", "&:hover": { bgcolor: "surfaceSoft" } },
      ]}
    >
      {children}
    </ButtonBase>
  );
}

function PageButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={[
        { borderRadius: 1, border: 1, px: 1, py: 0.25, fontSize: 10, fontWeight: 500 },
        disabled
          ? { borderColor: "transparent", color: "text.disabled", opacity: 0.5 }
          : { borderColor: "divider", color: "text.primary", "&:hover": { bgcolor: "surfaceSoft" } },
      ]}
    >
      {children}
    </ButtonBase>
  );
}

function kindLabel(k: SalesMissionKind): string {
  const map: Record<string, string> = {
    vertical_sweep:   "Vertical sweep",
    org_deepdive:     "Org deep-dive",
    signal_chase:     "Signal chase",
    tender_match:     "Tender match",
    competitor_watch: "Competitor watch",
    market_recon:     "Market recon",
    social_listening: "Social listening",
  };
  return map[k] ?? k;
}

function StatusPill({ status }: { status: SalesMissionStatus }): JSX.Element {
  // queued = muted; running = amber; done = green; failed = red — was
  // the `bg-*/15 text-*-700` class map.
  const m: { label: string; sx: SxStyle } = {
    pending: { label: "queued",  sx: { bgcolor: "surfaceSoft", color: "text.disabled" } },
    running: { label: "running", sx: { bgcolor: alpha(amber[500], 0.15), color: amber[800] } },
    done:    { label: "done",    sx: { bgcolor: alpha(green[500], 0.15), color: green[800] } },
    failed:  { label: "failed",  sx: { bgcolor: alpha(red[500], 0.15),   color: red[700] } },
  }[status];
  return (
    <Box
      component="span"
      sx={[
        { display: "inline-flex", alignItems: "center", borderRadius: 999, px: 1, py: 0.25, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },
        m.sx,
      ]}
    >
      {m.label}
    </Box>
  );
}
